import { createTheme } from '@mui/material';
import type { LoadingButtonProps } from '@mui/lab/LoadingButton';

declare module '@mui/material/styles' {
  interface Components {
    MuiLoadingButton?: {
      defaultProps?: Partial<LoadingButtonProps>;
      styleOverrides?: {
        root?: React.CSSProperties | Record<string, any>;
      };
    };
  }
}

const colors = {
  focusRing: '#2057A6',
  errorText: '#BD1A1A',
  disabledBg: '#D1D5DB',
  disabledText: '#1F2937',
  loadingBg: '#E5E7EB',
  loadingText: '#374151',
};

const commonFocusStyle = {
  '&.Mui-focusVisible': {
    outline: `2px solid ${colors.focusRing}`,
    outlineOffset: '2px',
    disableRipple: true,
    disableTouchRipple: true,
  },
};

const sharedButtonAccessibilityStyles = {
  '&.Mui-disabled': {
    backgroundColor: `${colors.disabledBg} !important`,
    color: `${colors.disabledText} !important`,
  },
};

/* OPTIMISED: Reusable sub-configuration map cleanly handles calendar sub-components to eliminate code duplication */
const basePickerSelectionOverrides = {
  ...commonFocusStyle,
  '&.Mui-selected': {
    backgroundColor: '#2057A6 !important',
    color: '#FFFFFF !important',
    '&:focus, &.Mui-focused, &.Mui-focusVisible': {
      backgroundColor:
        '#131836 !important' /* Balanced deep brand accent satisfies 3:1 non-text contrast limit */,
      outline: '2px solid #2057A6',
      outlineOffset: '2px',
    },
  },
};

const theme = createTheme({
  typography: {
    fontFamily: 'Work Sans, sans-serif',
  },
  palette: {
    primary: {
      main: '#2057A6',
    },
    secondary: {
      main: '#131836',
    },
    text: {
      primary: '#293139',
    },
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          maxWidth: '100%',
          width: '100%',
          '&.Mui-disabled': {
            color: '#757C81',
            WebkitTextFillColor: '#757C81',
          },
        },
        input: {
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          '&.MuiInputBase-inputMultiline': {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflow: 'auto',
            textOverflow: 'unset',
          },
          '&::placeholder': {
            color: '#60676D',
            opacity: 1,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          },
          '&.Mui-disabled': {
            color: '#757C81',
            WebkitTextFillColor: '#757C81',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            outline: '2px solid #2057A6',
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          color: '#fff',
          borderRadius: '5px',
          textTransform: 'none',
          fontWeight: 'bold',
          fontSize: '1.125rem',
          padding: '0.625rem',
          maxWidth: '100%',
          ...commonFocusStyle,
          ...sharedButtonAccessibilityStyles,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          padding: '4px',
          transition: 'none !important',
          '&.Mui-focusVisible': {
            backgroundColor: 'transparent !important',
            outline: '2px solid #2057A6 !important',
            outlineOffset: '2px !important',
            borderRadius: '4px !important',
          },
          '& input': {
            outline: 'none !important',
            outlineOffset: '0 !important',
            background: 'transparent !important',
            '&:focus': {
              outline: 'none !important',
            },
          },
        },
      },
    },
    MuiLoadingButton: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          color: '#fff',
          borderRadius: '5px',
          textTransform: 'none',
          fontWeight: 'bold',
          fontSize: '1.125rem',
          padding: '0.625rem',
          maxWidth: '100%',
          ...commonFocusStyle,
          ...sharedButtonAccessibilityStyles,

          '&.MuiLoadingButton-loading': {
            backgroundColor: `${colors.loadingBg} !important`,
            color: `${colors.loadingText} !important`,
            '& .MuiLoadingButton-loadingIndicator': {
              color: `${colors.loadingText} !important`,
            },
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          ...commonFocusStyle,
        },
      },
    },
    MuiMenuItem: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: { ...commonFocusStyle },
      },
    },
    MuiListItemButton: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          /* FIX: Darkened from #2A7F83 to #1D5A5D to exceed the 4.5:1 text foreground contrast limits on light layouts */
          color: '#1D5A5D',
          ...commonFocusStyle,
        },
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        'html, body': {
          maxWidth: '100%',
        },
        img: {
          maxWidth: '100%',
        },
        /* FIX: Solves the default enqueueSnackbar contrast validation failures globally across the build */
        '.SnackbarContent-root, .SnackbarItem-variantDefault': {
          backgroundColor: '#0B4627 !important',
          color:
            '#FFFFFF !important' /* Absolute crisp white text ensures 4.5:1 legibility compliance */,
          fontSize: '0.875rem !important',
          fontWeight: '500 !important',
          borderRadius: '6px !important',
          '& button, & a': {
            color: '#FFFFFF !important',
          },
        },
        '.warning-note': {
          transition: 'color 0.2s ease',
          '&:focus-visible': {
            color: colors.errorText,
            outline: `2px solid ${colors.focusRing}`,
            outlineOffset: '2px',
          },
        },
        '@media (max-width: 320px)': {
          'body, p, span, input, button': {
            wordBreak: 'break-word',
          },
          'input::placeholder, textarea::placeholder': {
            textOverflow: 'ellipsis !important',
            overflow: 'hidden !important',
            whiteSpace: 'nowrap !important',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.warning-note': {
            '&:focus-visible': {
              color: colors.errorText,
              outline: `2px solid ${colors.focusRing}`,
              outlineOffset: '2px',
            },
          },
        },
      },
    },
    ...{
      ['MuiPickersDay']: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: basePickerSelectionOverrides,
        },
      },
      ['MuiCalendarPicker']: {
        styleOverrides: {
          root: {
            '& button': { ...commonFocusStyle },
          },
        },
      },
      ['MuiMonthPicker']: {
        styleOverrides: {
          root: {
            '& .MuiTypography-root': {
              ...basePickerSelectionOverrides,
              borderRadius: '4px',
            },
          },
        },
      },
      ['MuiYearPicker']: {
        styleOverrides: {
          root: {
            '& .PrivatePickersYear-yearButton': basePickerSelectionOverrides,
          },
        },
      },
    },
  },
});

export default theme;
